import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Avatar from '../components/Avatar';
import { ApiError, tarotApi } from '../lib/api';
import { toApiLocale } from '../lib/locale';
import {
  applyPersonaTheme,
  lockedPersona,
  lockedTheme,
  PICKER_OPTIONS,
  themeForPersona,
  type Persona,
  type PickerOption,
  type Theme,
} from '../lib/personas';
import { PersonaMotif } from '../lib/themeEmblem';
import { useTheme } from '../lib/useTheme';

/**
 * /onboarding — 三段式 wizard（v2 視覺重做，邏輯不變）：
 *
 *   Step 1: Picker      4 卡（luna / orion / sage / silhouette）
 *   Step 2: Calibrate   只在點 silhouette 才出現，3 問校準 → /api/sessions/calibrate
 *                       回 recommended → 顯示「Use this」/ 「Pick differently」
 *   Step 3: Consent     PIPEDA / Law 25 同意 + Mock Google login
 *                       → /api/auth/mock-login → /api/sessions → navigate /session/:id
 *
 * v2 視覺改動：
 *   - 進度指示用 dots（不是 bar）
 *   - Picker 卡：persona theme 漸層 + abstract SVG motif（取代 4/5 色塊 placeholder）
 *   - 字級採用 var(--h1-tracking)，標題用 font-display
 *   - 同意書/校準步驟維持原邏輯，只升級 chrome
 */

type Step = 'pick' | 'calibrate' | 'consent';
type Answer = 'a' | 'b' | 'c';

const CALIBRATE_QUESTIONS = ['q1', 'q2', 'q3'] as const;
const CALIBRATE_OPTIONS: Answer[] = ['a', 'b', 'c'];

function clientFallbackRecommend(answers: Answer[]): Persona {
  const counts: Record<Answer, number> = { a: 0, b: 0, c: 0 };
  for (const a of answers) counts[a]++;
  const top = (Object.entries(counts) as [Answer, number][]).sort((a, b) => b[1] - a[1])[0]?.[0];
  if (top === 'a') return 'luna';
  if (top === 'b') return 'orion';
  return 'sage';
}

export default function Onboarding() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('pick');
  const [pickedOption, setPickedOption] = useState<PickerOption | null>(null);
  const [answers, setAnswers] = useState<Array<Answer | null>>([null, null, null]);
  const [recommended, setRecommended] = useState<Persona | null>(null);
  const [calibrating, setCalibrating] = useState(false);
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const readerLock = lockedPersona();
  const themeLock = lockedTheme();

  const finalPersona = useMemo<Persona | null>(() => {
    if (readerLock) return readerLock;
    if (pickedOption && pickedOption !== 'silhouette') return pickedOption;
    if (recommended) return recommended;
    return null;
  }, [pickedOption, readerLock, recommended]);

  // 預覽用 theme：picker 選了之後，先把畫面切成那個 theme
  useEffect(() => {
    if (!finalPersona) return;
    if (themeLock) {
      document.documentElement.setAttribute('data-theme', themeLock);
      return;
    }
    applyPersonaTheme(finalPersona);
  }, [finalPersona, themeLock]);

  function handlePick(opt: PickerOption) {
    if (readerLock && opt !== readerLock) return;
    setError(null);
    setPickedOption(opt);
    if (opt === 'silhouette') {
      setStep('calibrate');
    } else {
      setStep('consent');
    }
  }

  async function submitCalibration() {
    setError(null);
    if (answers.some((a) => a === null)) return;
    setCalibrating(true);
    try {
      const resp = await tarotApi.calibrate(answers as Answer[]);
      setRecommended(resp.recommended);
    } catch (e) {
      console.warn('[onboarding] calibrate fallback', e);
      setRecommended(clientFallbackRecommend(answers as Answer[]));
    } finally {
      setCalibrating(false);
    }
  }

  function pickDifferently() {
    setRecommended(null);
    setAnswers([null, null, null]);
    setPickedOption(null);
    setStep('pick');
  }

  async function startSession() {
    if (!finalPersona || !consent) return;
    setSubmitting(true);
    setError(null);
    try {
      try {
        await tarotApi.mockLogin();
      } catch (e) {
        console.warn('[onboarding] mock-login skipped (backend offline)', e);
      }

      const theme = themeForPersona(finalPersona);
      const session = await tarotApi.createSession({
        persona: finalPersona,
        theme,
        locale: toApiLocale(i18n.resolvedLanguage),
        consent_version: 'v1',
      });
      document.documentElement.setAttribute('data-theme', theme);
      navigate(`/session/${session.id}`);
    } catch (e) {
      if (e instanceof ApiError && e.status >= 500) {
        setError(t('onboarding.errors.backend'));
      } else {
        setError(t('onboarding.errors.session'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="mx-auto w-full max-w-5xl px-6 pb-20 pt-24 sm:pt-28">
      <Link to="/" className="mb-8 inline-block text-sm text-muted hover:text-fg">
        ← {t('common.nav.back')}
      </Link>

      <ProgressDots step={step} />

      {step === 'pick' && (
        <PickerStep pickedOption={pickedOption} readerLock={readerLock} themeLock={themeLock} onPick={handlePick} />
      )}

      {step === 'calibrate' && (
        <CalibrateStep
          answers={answers}
          onAnswer={(idx, ans) =>
            setAnswers((prev) => {
              const next = [...prev];
              next[idx] = ans;
              return next;
            })
          }
          calibrating={calibrating}
          recommended={recommended}
          onSubmit={submitCalibration}
          onUseThis={() => setStep('consent')}
          onPickDifferently={pickDifferently}
        />
      )}

      {step === 'consent' && finalPersona && (
        <ConsentStep
          persona={finalPersona}
          consent={consent}
          onConsentChange={setConsent}
          submitting={submitting}
          onStart={startSession}
          error={error}
        />
      )}
    </main>
  );
}

// ---------------------------------------------------------------------------
// ProgressDots — 不是 bar，是 spec §5 要求的 dots
// ---------------------------------------------------------------------------

function ProgressDots({ step }: { step: Step }) {
  const order: Step[] = ['pick', 'calibrate', 'consent'];
  const idx = order.indexOf(step);
  return (
    <div className="mb-10 flex items-center gap-3 text-xs text-muted">
      {order.map((s, i) => (
        <div key={s} className="flex items-center gap-3">
          <span
            className="h-2 w-2 rounded-full transition-colors"
            style={{
              background:
                i <= idx ? 'var(--color-accent)' : 'var(--color-border-hi)',
            }}
            aria-current={i === idx ? 'step' : undefined}
          />
          {i < order.length - 1 && <span className="h-px w-8 bg-border" />}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// PickerStep — 4 卡，每卡 persona theme 漸層 + SVG motif
// ---------------------------------------------------------------------------

function PickerStep({
  pickedOption,
  readerLock,
  themeLock,
  onPick,
}: {
  pickedOption: PickerOption | null;
  readerLock: Persona | null;
  themeLock: Theme | null;
  onPick: (opt: PickerOption) => void;
}) {
  const { t } = useTranslation();
  return (
    <section>
      <header className="mb-12 max-w-2xl">
        <p className="mb-3 font-mono text-[0.65rem] uppercase tracking-[0.22em] text-accent">
          {t('onboarding.picker.kicker')}
        </p>
        <h1
          className="text-balance font-display text-4xl leading-tight text-heading sm:text-5xl"
          style={{ letterSpacing: 'var(--h1-tracking)' }}
        >
          {t('onboarding.picker.title')}
        </h1>
        <p className="mt-4 text-pretty text-base text-muted">
          {t('onboarding.picker.subtitle')}
        </p>
      </header>

      <ul className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {PICKER_OPTIONS.map((opt) => {
          const isPicked = pickedOption === opt;
          const lockedOut = Boolean(readerLock && opt !== readerLock);
          return (
            <li key={opt}>
              <button
                type="button"
                disabled={lockedOut}
                onClick={() => onPick(opt)}
                className={[
                  'group flex w-full flex-col overflow-hidden border bg-surface text-left transition-all',
                  lockedOut ? 'cursor-not-allowed opacity-38' : '',
                  isPicked
                    ? 'border-accent shadow-card'
                    : 'border-border hover:border-border-hi hover:shadow-card',
                ].join(' ')}
                style={{ borderRadius: 'var(--radius-card)' }}
              >
                <PickerMotif persona={opt} />
                <div className="flex flex-1 flex-col gap-2 p-5">
                  <div className="flex items-baseline justify-between gap-2">
                    <h2 className="font-display text-xl text-heading">
                      {t(`onboarding.picker.options.${opt}.name`)}
                    </h2>
                    <span className="font-mono text-[0.65rem] uppercase tracking-[0.18em] text-accent">
                      {t(`onboarding.picker.options.${opt}.tagline`)}
                    </span>
                  </div>
                  <p className="text-pretty text-sm leading-relaxed text-muted">
                    {t(`onboarding.picker.options.${opt}.blurb`)}
                  </p>
                </div>
              </button>
            </li>
          );
        })}
      </ul>
      {(readerLock || themeLock) && (
        <p className="mt-5 w-fit border border-border bg-elevated px-3 py-2 font-mono text-[0.6rem] uppercase tracking-[0.16em] text-accent" style={{ borderRadius: 'var(--radius-card)' }}>
          SKU lock · {readerLock ?? 'reader open'} · {themeLock ?? 'theme by reader'}
        </p>
      )}
    </section>
  );
}

/**
 * Picker 上半部 motif — 每 persona 用 persona 自己 theme 的色票 gradient（不管 root theme）。
 * 這是 spec 點名要的：「每卡背景用 persona theme 的色票漸層 + abstract motif」
 */
function PickerMotif({ persona }: { persona: PickerOption }) {
  const recipe = MOTIF_RECIPES[persona];
  return (
    <div
      className="relative flex aspect-[4/5] w-full items-center justify-center overflow-hidden"
      style={{ background: recipe.bg, color: recipe.ink }}
      aria-hidden
    >
      <PersonaMotif persona={persona} className="h-1/2 w-1/2" />
    </div>
  );
}

const MOTIF_RECIPES: Record<
  PickerOption,
  { bg: string; ink: string }
> = {
  // luna → morandi 色票
  luna: {
    bg: 'linear-gradient(160deg, #f7f1e8 0%, #e8d6c8 60%, #c9a89a 100%)',
    ink: '#3a302a',
  },
  // orion → mystic 色票
  orion: {
    bg: 'radial-gradient(ellipse at 50% 30%, #2a2238 0%, #0d0b14 75%)',
    ink: '#e8dcb9',
  },
  // sage → minimal 色票
  sage: {
    bg: 'repeating-linear-gradient(135deg, #faf7f2 0 14px, #f1ede5 14px 15px)',
    ink: '#0a0a0a',
  },
  // silhouette → 中性深灰
  silhouette: {
    bg: 'linear-gradient(160deg, #2a2a2a 0%, #4a4a4a 60%, #6a6a6a 100%)',
    ink: '#e8e8e8',
  },
};

// ---------------------------------------------------------------------------
// CalibrateStep — 邏輯不變，視覺升級
// ---------------------------------------------------------------------------

function CalibrateStep({
  answers,
  onAnswer,
  calibrating,
  recommended,
  onSubmit,
  onUseThis,
  onPickDifferently,
}: {
  answers: Array<Answer | null>;
  onAnswer: (idx: number, ans: Answer) => void;
  calibrating: boolean;
  recommended: Persona | null;
  onSubmit: () => void;
  onUseThis: () => void;
  onPickDifferently: () => void;
}) {
  const { t } = useTranslation();
  const allAnswered = answers.every((a) => a !== null);

  if (recommended) {
    return (
      <section className="mx-auto max-w-2xl">
        <header className="mb-10 text-center">
          <p className="mb-3 font-mono text-[0.65rem] uppercase tracking-[0.22em] text-accent">
            {t('onboarding.calibrate.resultKicker')}
          </p>
          <h1
            className="font-display text-3xl leading-tight text-heading sm:text-4xl"
            style={{ letterSpacing: 'var(--h1-tracking)' }}
          >
            {t('onboarding.calibrate.resultTitle', {
              name: t(`onboarding.picker.options.${recommended}.name`),
            })}
          </h1>
        </header>

        <div
          className="flex flex-col items-center gap-5 border border-accent bg-surface p-8 text-center"
          style={{ borderRadius: 'var(--radius-card)' }}
        >
          <Avatar persona={recommended} className="h-24 w-24" alt={recommended} />
          <div>
            <h2 className="font-display text-2xl text-heading">
              {t(`onboarding.picker.options.${recommended}.name`)}
            </h2>
            <p className="mt-1 font-mono text-[0.65rem] uppercase tracking-[0.22em] text-accent">
              {t(`onboarding.picker.options.${recommended}.tagline`)}
            </p>
            <p className="mt-3 text-pretty text-sm leading-relaxed text-muted">
              {t(`onboarding.calibrate.reasoning.${recommended}`)}
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            <button
              type="button"
              onClick={onUseThis}
              className="px-6 py-2.5 text-sm font-medium transition-opacity hover:opacity-90"
              style={{
                background: 'var(--btn-bg)',
                color: 'var(--btn-ink)',
                border: '1px solid var(--btn-border, transparent)',
                borderRadius: 'var(--radius-pill)',
                boxShadow: 'var(--btn-shadow)',
              }}
            >
              {t('onboarding.calibrate.useThis')}
            </button>
            <button
              type="button"
              onClick={onPickDifferently}
              className="rounded-pill border border-border px-6 py-2.5 text-sm transition-colors hover:bg-elevated"
            >
              {t('onboarding.calibrate.pickDifferently')}
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-2xl">
      <header className="mb-10">
        <p className="mb-3 font-mono text-[0.65rem] uppercase tracking-[0.22em] text-accent">
          {t('onboarding.calibrate.kicker')}
        </p>
        <h1
          className="font-display text-3xl leading-tight text-heading sm:text-4xl"
          style={{ letterSpacing: 'var(--h1-tracking)' }}
        >
          {t('onboarding.calibrate.title')}
        </h1>
        <p className="mt-3 text-pretty text-base text-muted">
          {t('onboarding.calibrate.subtitle')}
        </p>
      </header>

      <ol className="space-y-6">
        {CALIBRATE_QUESTIONS.map((q, idx) => (
          <li
            key={q}
            className="border border-border bg-surface p-6"
            style={{ borderRadius: 'var(--radius-card)' }}
          >
            <p className="mb-2 font-mono text-[0.65rem] uppercase tracking-[0.18em] text-muted">
              {t('onboarding.calibrate.questionLabel', { n: idx + 1 })}
            </p>
            <p className="mb-5 font-display text-lg leading-snug text-heading">
              {t(`onboarding.calibrate.questions.${q}.prompt`)}
            </p>
            <div className="grid gap-2">
              {CALIBRATE_OPTIONS.map((opt) => {
                const checked = answers[idx] === opt;
                return (
                  <label
                    key={opt}
                    className={[
                      'flex cursor-pointer items-start gap-3 border p-3 text-sm transition-colors',
                      checked
                        ? 'border-accent bg-elevated'
                        : 'border-border hover:border-border-hi',
                    ].join(' ')}
                    style={{ borderRadius: 'var(--radius-card)' }}
                  >
                    <input
                      type="radio"
                      name={`q-${q}`}
                      value={opt}
                      checked={checked}
                      onChange={() => onAnswer(idx, opt)}
                      className="mt-0.5 accent-current"
                    />
                    <span className="text-pretty leading-relaxed">
                      {t(`onboarding.calibrate.questions.${q}.options.${opt}`)}
                    </span>
                  </label>
                );
              })}
            </div>
          </li>
        ))}
      </ol>

      <div className="mt-8 flex justify-end">
        <button
          type="button"
          disabled={!allAnswered || calibrating}
          onClick={onSubmit}
          className="px-6 py-2.5 text-sm font-medium transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
          style={{
            background: 'var(--btn-bg)',
            color: 'var(--btn-ink)',
            border: '1px solid var(--btn-border, transparent)',
            borderRadius: 'var(--radius-pill)',
            boxShadow: 'var(--btn-shadow)',
          }}
        >
          {calibrating ? t('onboarding.calibrate.submitting') : t('onboarding.calibrate.submit')}
        </button>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// ConsentStep — 不動 mock OAuth 流程，只升級 chrome
// ---------------------------------------------------------------------------

function ConsentStep({
  persona,
  consent,
  onConsentChange,
  submitting,
  onStart,
  error,
}: {
  persona: Persona;
  consent: boolean;
  onConsentChange: (v: boolean) => void;
  submitting: boolean;
  onStart: () => void;
  error: string | null;
}) {
  const { t } = useTranslation();
  // 觸發 re-render 在 theme 同步後（雖然這裡沒直接用 theme，但 ensure refresh）
  useTheme();

  return (
    <section className="mx-auto max-w-2xl">
      <header className="mb-10 text-center">
        <p className="mb-3 font-mono text-[0.65rem] uppercase tracking-[0.22em] text-accent">
          {t('onboarding.consent.kicker')}
        </p>
        <h1
          className="font-display text-3xl leading-tight text-heading sm:text-4xl"
          style={{ letterSpacing: 'var(--h1-tracking)' }}
        >
          {t('onboarding.consent.title', {
            name: t(`onboarding.picker.options.${persona}.name`),
          })}
        </h1>
        <p className="mt-3 text-pretty text-base text-muted">{t('onboarding.consent.subtitle')}</p>
      </header>

      <div
        className="mb-6 flex flex-col items-center gap-4 border border-border bg-surface p-6"
        style={{ borderRadius: 'var(--radius-card)' }}
      >
        <Avatar persona={persona} className="h-20 w-20" alt={persona} />
        <div className="text-center">
          <h2 className="font-display text-xl text-heading">
            {t(`onboarding.picker.options.${persona}.name`)}
          </h2>
          <p className="font-mono text-[0.65rem] uppercase tracking-[0.22em] text-accent">
            {t(`onboarding.picker.options.${persona}.tagline`)}
          </p>
        </div>
      </div>

      <label
        className="mb-6 flex cursor-pointer items-start gap-3 border border-border bg-surface p-5 text-sm leading-relaxed"
        style={{ borderRadius: 'var(--radius-card)' }}
      >
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => onConsentChange(e.target.checked)}
          className="mt-1 accent-current"
        />
        <span className="text-pretty">
          <span className="font-semibold text-heading">{t('onboarding.consent.checkbox.title')}</span>
          <span className="mt-1 block text-muted">{t('onboarding.consent.checkbox.body')}</span>
          <Link
            to="/data-residency"
            className="mt-2 inline-block text-xs text-accent hover:underline"
            target="_blank"
            rel="noreferrer"
          >
            {t('onboarding.consent.learnMore')} →
          </Link>
        </span>
      </label>

      {error && (
        <p
          role="alert"
          className="mb-4 border border-accent bg-elevated px-4 py-3 text-sm text-accent"
          style={{ borderRadius: 'var(--radius-card)' }}
        >
          {error}
        </p>
      )}

      <div className="flex flex-col gap-3">
        <button
          type="button"
          disabled={!consent || submitting}
          onClick={onStart}
          className="flex w-full items-center justify-center gap-2 px-6 py-3 text-sm font-medium transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
          style={{
            background: 'var(--btn-bg)',
            color: 'var(--btn-ink)',
            border: '1px solid var(--btn-border, transparent)',
            borderRadius: 'var(--radius-pill)',
            boxShadow: 'var(--btn-shadow)',
          }}
        >
          <GoogleGlyph />
          {submitting ? t('onboarding.consent.starting') : t('onboarding.consent.googleLogin')}
        </button>
        <p className="text-center text-xs text-muted">{t('onboarding.consent.googleNote')}</p>
      </div>
    </section>
  );
}

function GoogleGlyph() {
  // 純線稿 G — 不用 emoji，極簡 outline
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden>
      <path
        d="M21 12c0 5-4 9-9 9s-9-4-9-9 4-9 9-9c2.4 0 4.6.95 6.2 2.5l-2.5 2.5C14.6 7 13.4 6.5 12 6.5c-3 0-5.5 2.5-5.5 5.5s2.5 5.5 5.5 5.5c2.6 0 4.7-1.7 5.2-4H12v-3h9v1.5z"
        fill="currentColor"
        opacity="0.95"
      />
    </svg>
  );
}
